import type {StateFilter} from '@github-ui/shared-dashboard-lists/shared/types/list-types'

import {tokenizeQuery, type QueryToken} from './query-tokenizer'

const STATE_FILTER_PREFIX = 'state:'
const LEGACY_STATE_FILTER_PREFIX = 'is:'

type QueryNode =
  | {kind: 'term'; value: string}
  | {kind: 'and' | 'or'; children: QueryNode[]}
  | {kind: 'not'; child: QueryNode}

type BooleanOperator = 'AND' | 'OR' | 'NOT'

export type StateQueryAnalysis = {kind: 'none'} | {kind: 'selected'; state: StateFilter} | {kind: 'ambiguous'}

type StateQueryOptions = {
  legacyAliases?: boolean
}

type StateFilterUpdateOptions = {
  /** For flat, non-Boolean queries, move state filters before the other terms. */
  placement?: 'start'
}

function operatorKind(token: QueryToken | undefined): BooleanOperator | undefined {
  if (token?.kind !== 'term') return undefined

  const normalizedValue = token.value.toUpperCase()
  return normalizedValue === 'AND' || normalizedValue === 'OR' || normalizedValue === 'NOT'
    ? normalizedValue
    : undefined
}

function isStateFilter(token: QueryToken): boolean {
  return token.kind === 'term' && getQualifierValue(token.value, STATE_FILTER_PREFIX) !== undefined
}

function isLegacyStateFilter(token: QueryToken): boolean {
  return token.kind === 'term' && getLegacyStateFilter(token.value) !== undefined
}

function getStateFilterValue(value: string): StateFilter | undefined {
  const normalizedValue = value.toLowerCase()
  if (normalizedValue === 'open' || normalizedValue === '"open"') return 'open'
  if (normalizedValue === 'closed' || normalizedValue === '"closed"') return 'closed'
  return undefined
}

function getQualifierValue(value: string, prefix: string): string | undefined {
  if (value.length <= prefix.length || value.slice(0, prefix.length).toLowerCase() !== prefix) return undefined
  return value.slice(prefix.length)
}

function getExcludedQualifierValue(value: string, prefix: string): string | undefined {
  return value.startsWith('-') ? getQualifierValue(value.slice(1), prefix) : undefined
}

function getLegacyStateFilter(value: string): StateFilter | undefined {
  const filterValue = getQualifierValue(value, LEGACY_STATE_FILTER_PREFIX)
  return filterValue ? getStateFilterValue(filterValue) : undefined
}

function hasTopLevelOr(tokens: QueryToken[]): boolean {
  let depth = 0

  for (const token of tokens) {
    if (token.kind === 'parenthesis') {
      depth += token.value === '(' ? 1 : -1
    } else if (depth === 0 && operatorKind(token) === 'OR') {
      return true
    }
  }

  return false
}

function isWrappedExpression(tokens: QueryToken[]): boolean {
  const significantTokens = tokens.filter(token => token.kind !== 'separator')
  if (significantTokens[0]?.value !== '(' || significantTokens.at(-1)?.value !== ')') return false

  let depth = 0
  for (const [index, token] of significantTokens.entries()) {
    if (token.kind !== 'parenthesis') continue
    depth += token.value === '(' ? 1 : -1
    if (depth === 0 && index < significantTokens.length - 1) return false
  }

  return depth === 0
}

function insertStateFilter(query: string, stateFilter: string): string {
  const tokens = tokenizeQuery(query, true)
  const isSimpleImplicitConjunction = tokens.every(
    token => token.kind === 'separator' || (token.kind === 'term' && operatorKind(token) === undefined),
  )

  if (isSimpleImplicitConjunction) {
    let termCount = 0
    const thirdTermIndex = tokens.findIndex(token => token.kind === 'term' && ++termCount === 3)
    if (thirdTermIndex === -1) return `${query} ${stateFilter}`

    tokens.splice(thirdTermIndex, 0, {kind: 'term', value: stateFilter}, {kind: 'separator', value: ' '})
    return tokens.map(token => token.value).join('')
  }

  const expression = hasTopLevelOr(tokens) && !isWrappedExpression(tokens) ? `(${query})` : query
  return `${expression} AND ${stateFilter}`
}

function addStateFilter(
  query: string | undefined,
  state: StateFilter,
  legacyAliases: boolean,
  {placement}: StateFilterUpdateOptions = {},
): string {
  const stateFilter = `${STATE_FILTER_PREFIX}${state}`

  if (!query) return stateFilter

  const tokens = tokenizeQuery(query, placement !== 'start')
  const stateTokens = new Set(
    tokens.filter(token => isStateFilter(token) || (legacyAliases && isLegacyStateFilter(token))),
  )

  if (placement === 'start') {
    const otherTerms = tokens
      .filter(token => token.kind === 'term' && !stateTokens.has(token))
      .map(token => token.value)
    return [stateFilter, ...otherTerms].join(' ')
  }

  if (stateTokens.size > 0) {
    for (const token of stateTokens) token.value = stateFilter
    return tokens.map(token => token.value).join('')
  }

  return insertStateFilter(query, stateFilter)
}

export function addStateToQuery(
  query: string | undefined,
  state: StateFilter,
  options?: StateFilterUpdateOptions,
): string {
  return addStateFilter(query, state, false, options)
}

export function addStateToQueryWithLegacyAliases(
  query: string | undefined,
  state: StateFilter,
  options?: StateFilterUpdateOptions,
): string {
  return addStateFilter(query, state, true, options)
}

class QueryParser {
  #index = 0
  readonly #tokens: QueryToken[]

  constructor(tokens: QueryToken[]) {
    this.#tokens = tokens
  }

  parse(): QueryNode | undefined {
    const node = this.#parseOr()
    return node && this.#index === this.#tokens.length ? node : undefined
  }

  #parseOr(): QueryNode | undefined {
    const first = this.#parseAnd()
    if (!first) return undefined

    const children = [first]
    while (operatorKind(this.#currentToken()) === 'OR') {
      this.#index++
      const child = this.#parseAnd()
      if (!child) return undefined
      children.push(child)
    }

    return children.length === 1 ? first : {kind: 'or', children}
  }

  #parseAnd(): QueryNode | undefined {
    const first = this.#parseUnary()
    if (!first) return undefined

    const children = [first]
    while (
      this.#index < this.#tokens.length &&
      this.#currentValue() !== ')' &&
      operatorKind(this.#currentToken()) !== 'OR'
    ) {
      if (operatorKind(this.#currentToken()) === 'AND') this.#index++
      const child = this.#parseUnary()
      if (!child) return undefined
      children.push(child)
    }

    return children.length === 1 ? first : {kind: 'and', children}
  }

  #parseUnary(): QueryNode | undefined {
    if (operatorKind(this.#currentToken()) === 'NOT') {
      this.#index++
      const child = this.#parseUnary()
      return child ? {kind: 'not', child} : undefined
    }

    const token = this.#tokens[this.#index]
    if (!token || operatorKind(token) === 'AND' || operatorKind(token) === 'OR' || token.value === ')') return undefined

    if (token.value === '(') {
      this.#index++
      const child = this.#parseOr()
      if (!child || this.#currentValue() !== ')') return undefined
      this.#index++
      return child
    }

    this.#index++
    return {kind: 'term', value: token.value}
  }

  #currentToken(): QueryToken | undefined {
    return this.#tokens[this.#index]
  }

  #currentValue(): string | undefined {
    return this.#currentToken()?.value
  }
}

function analyzeTerm(value: string, legacyAliases: boolean): StateQueryAnalysis {
  if (getExcludedQualifierValue(value, STATE_FILTER_PREFIX) !== undefined) return {kind: 'ambiguous'}

  const stateFilterValue = getQualifierValue(value, STATE_FILTER_PREFIX)
  if (stateFilterValue !== undefined) {
    const state = getStateFilterValue(stateFilterValue)
    return state ? {kind: 'selected', state} : {kind: 'ambiguous'}
  }

  if (legacyAliases) {
    const state = getLegacyStateFilter(value)
    if (state) return {kind: 'selected', state}
    const excludedLegacyValue = getExcludedQualifierValue(value, LEGACY_STATE_FILTER_PREFIX)
    if (excludedLegacyValue && getStateFilterValue(excludedLegacyValue)) return {kind: 'ambiguous'}
  }

  return {kind: 'none'}
}

function analyzeNode(node: QueryNode, legacyAliases: boolean): StateQueryAnalysis {
  if (node.kind === 'term') return analyzeTerm(node.value, legacyAliases)

  if (node.kind === 'not') {
    return analyzeNode(node.child, legacyAliases).kind === 'none' ? {kind: 'none'} : {kind: 'ambiguous'}
  }

  const analyses = node.children.map(child => analyzeNode(child, legacyAliases))
  if (analyses.some(analysis => analysis.kind === 'ambiguous')) return {kind: 'ambiguous'}

  const selected = analyses.filter(
    (analysis): analysis is {kind: 'selected'; state: StateFilter} => analysis.kind === 'selected',
  )
  if (selected.length === 0) return {kind: 'none'}

  const state = selected[0]?.state
  if (!state || selected.some(analysis => analysis.state !== state)) return {kind: 'ambiguous'}
  if (node.kind === 'or' && selected.length !== analyses.length) return {kind: 'ambiguous'}

  return {kind: 'selected', state}
}

export function analyzeStateQuery(
  query: string | undefined,
  {legacyAliases = false}: StateQueryOptions = {},
): StateQueryAnalysis {
  if (!query) return {kind: 'none'}

  const tokens = tokenizeQuery(query, true).filter(token => token.kind !== 'separator')
  const node = new QueryParser(tokens).parse()
  if (node) return analyzeNode(node, legacyAliases)

  const containsStateConstraint = tokens.some(token => analyzeTerm(token.value, legacyAliases).kind !== 'none')
  return containsStateConstraint ? {kind: 'ambiguous'} : {kind: 'none'}
}

export function getStateFromQuery(query: string | undefined): StateFilter | undefined {
  const analysis = analyzeStateQuery(query)
  return analysis.kind === 'selected' ? analysis.state : undefined
}
